import QRCode from 'qrcode';

/**
 * QR code generation utilities for field stickers.
 *
 * URL composition matches the canonical v1 sticker format that
 * production hardware carries today (parsed by
 * apps/web/src/lib/qr.ts):
 *
 *   https://<PWA_HOST>/charging-station/<locationId>?charger=<chargerIdentity>
 *
 * PWA host is read from VITE_PUBLIC_PWA_HOST at admin build time
 * (defaults to `app.evinitiative.com` if unset). Setting it to
 * `app.stg.evinitiative.com` for stage builds keeps test QRs from
 * accidentally routing to production.
 *
 * Generation runs entirely client-side — qrcode lib is ~41kB
 * gzipped and zero-dep. The resulting URL is non-sensitive (same
 * URL the field sticker carries; identity + location id are
 * already exposed via the QR), so there's no auth surface to
 * worry about.
 */

const PWA_HOST = (import.meta.env['VITE_PUBLIC_PWA_HOST'] as string | undefined)
  ?? 'app.evinitiative.com';

/**
 * Compose the canonical QR target URL for a given charger.
 *
 * @param chargerIdentity OCPP identity (e.g. `CU89TVWKPVJ8`).
 * @param locationId Mongo ObjectId hex of the location.
 *
 * Identity and locationId are URL-encoded defensively — the schema
 * regex on identity is `[A-Za-z0-9_-]` so it's URL-safe today, but
 * encoding is free insurance against a future schema relaxation
 * landing a slash or space in the field.
 */
export function chargerUrl(chargerIdentity: string, locationId: string): string {
  return `https://${PWA_HOST}/charging-station/${encodeURIComponent(locationId)}?charger=${encodeURIComponent(chargerIdentity)}`;
}

/**
 * Generate a PNG QR code as a Blob ready for download.
 *
 * Default size 1024px gives crisp output at print sizes up to a
 * standard 4x4 inch sticker (300dpi); the qrcode lib upscales the
 * matrix without anti-aliasing artifacts because QR symbols are
 * binary and don't blur on integer scales.
 *
 * Error correction `M` (15% redundancy) is the qrcode default and
 * is correct for outdoor field stickers where partial dirt or
 * scratches are realistic. Higher levels (Q, H) increase visual
 * density without meaningful gain at the URL length we encode.
 */
export async function generateQrPng(
  url: string,
  options?: { size?: number },
): Promise<Blob> {
  const size = options?.size ?? 1024;
  const dataUrl = await QRCode.toDataURL(url, {
    type: 'image/png',
    width: size,
    margin: 2,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#FFFFFF' },
  });
  // Convert data URL → Blob without going through fetch (which
  // works but is heavyweight for a synchronous-ish data URL).
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  // Cast to BlobPart works around the strict
  // Uint8Array<ArrayBufferLike> vs ArrayBufferView<ArrayBuffer>
  // narrowing TS5 introduced for the Shared/ArrayBuffer split.
  // The runtime contract is unchanged; this is a TS-only annotation.
  return new Blob([bytes as BlobPart], { type: 'image/png' });
}

/**
 * Generate an SVG QR code as a string. Used for inline modal
 * previews where vector rendering scales without rasterization
 * artifacts.
 */
export async function generateQrSvg(url: string): Promise<string> {
  return QRCode.toString(url, {
    type: 'svg',
    margin: 2,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#FFFFFF' },
  });
}

/**
 * Generate the raw QR matrix as an array of bytes for embedding in
 * pdf-lib. pdf-lib doesn't parse SVG natively, so the PDF generator
 * uses the PNG variant via embedPng. This helper exists for any
 * future PDF code that wants SVG-vector rendering — currently
 * unused but exported for parity with generateQrPng.
 */
export async function generateQrPngForPdf(url: string, size: number): Promise<Uint8Array> {
  const dataUrl = await QRCode.toDataURL(url, {
    type: 'image/png',
    width: size,
    margin: 2,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#FFFFFF' },
  });
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
